import { useSnackbar } from '@client/hooks/use-snackbar.hooks';
import { Button } from '@ui/buttons';
import { EditableHistoryComment } from './EditableHistoryComment';
import React, {
  Fragment,
  useCallback,
  useLayoutEffect,
  useMemo,
  useRef,
  useState,
  type KeyboardEvent,
} from 'react';
import { Box, IconButton, InputBase, Tooltip, Typography } from '@mui/material';
import { North, KeyboardArrowRight } from '@mui/icons-material';
import { makeStyles } from 'tss-react/mui';
import { getTooltipBaseStyles } from '@client/style/tooltip.styles';
import { useUser, useUserLookup } from '@client/hooks/use-user.hooks';
import { useProjectNavigation } from '@client/hooks/use-router.hooks';
import { PROPOSAL_SEARCH_PARAM } from '@client/hooks/use-selected-proposal-id.hooks';
import { URL_CATEGORY_NONE } from '@client/models/router.model';
import { Page } from '@client/store/ui/ui.model';
import { VerticalFadeScrollbar } from '@ui/layout/MainLayoutScrollbars';
import { getProject, useProject } from '@client/store/project';
import { getProposals } from '@shared/helpers/proposals';
import { getElementName } from '@shared/helpers/element.helpers';
import { getElementCategory } from '@shared/helpers/element-category.helpers';
import {
  findElement,
  getParentElement,
} from '@shared/helpers/recursive-element.helpers';
import { getElementIconCategoryName } from '@features/elements/helpers/get-element-category-icon-name.helper';
import { Icon } from '@ui/Icon';
import { ElementKind, type Project } from '@shared/models/project.model';
import { buildActionName } from '@shared/helpers/actions/action.helpers';
import { getBuildingDisplayName } from '@shared/helpers/design/building.helpers';
import {
  getStoreyNumber,
  isEmptyBuildingGeometry,
} from '@shared/helpers/design/storey.helpers';
import { useBuildingDesign } from '@features/design/hooks/design.hooks';
import { useSelectBuilding } from '@features/design/hooks/storeys.hooks';
import {
  useSelectBuildingOutline,
  useSetDesignRightPanelTab,
  useSetSelectedStoreyId,
} from '@features/design/store/design-store.hooks';
import type { ProjectEventActivity } from '@shared/models/project-event.model';
import { useProjectEvents } from '@client/store/project/project.hook';
import { footprintStoreyIds } from '@shared/helpers/project-events/project-events.helpers';
import { assertUnreachable } from '@shared/helpers/function.helpers';
import { useCommentsStore } from '@client/store/comment/comment.store';
import {
  formatChatMessageTime,
  shouldShowChatTimestamp,
} from '@features/ai-chat/ai-chat-timestamp.helpers';
import {
  commentBubbleRun,
  eventThreadItem,
  footprintStoreyPhrase,
  historyTimestampLabel,
  isSummaryOnlyActivity,
  type CommentThreadItem,
} from './design-comments-thread.helpers';
import { OPEN_AI_USER_ID } from '@shared/constants';
import { commentSeenKey } from '@features/design/components/use-design-comments-unread.hook';
import { createUserComment } from '@shared/helpers/comment.helpers';
import { IComment } from '@shared/models/comment.model';

const DESIGN_COMMENTS_INPUT_HEIGHT_PROPERTY = '--design-comments-input-height';
const CREATE_ERROR_ID = 'design-panel-comment';

const commentDraftKey = (projectId: number): string =>
  `nodon:design-comment:draft:${projectId}`;

const readCommentDraft = (projectId: number): string =>
  localStorage.getItem(commentDraftKey(projectId)) ?? '';

const writeCommentDraft = (projectId: number, text: string): void => {
  const key = commentDraftKey(projectId);
  if (text.trim()) {
    localStorage.setItem(key, text);
    return;
  }
  localStorage.removeItem(key);
};

type DraftChangeHandler = (value: string) => void;
type CommentHoverHandler = (commentId: string) => void;

const isUserComment = (comment: IComment): boolean =>
  comment.owner !== OPEN_AI_USER_ID && comment.message.trim().length > 0;

const elementNameSentenceCase = (name: string): string => {
  const lower = name.toLocaleLowerCase('sv');
  return lower.charAt(0).toLocaleUpperCase('sv') + lower.slice(1);
};

const elementCommentContext = (
  project: Project,
  elementId: string,
):
  | {
      name: string;
      icon: ReturnType<typeof getElementIconCategoryName>;
      versionId: string;
    }
  | undefined => {
  for (const version of project.baselines) {
    const element = findElement(version, elementId);
    if (!element) continue;
    const categorySource =
      element.kind === ElementKind.Product
        ? (getParentElement(version, element) ?? element)
        : element;
    return {
      name: elementNameSentenceCase(getElementName(element, version.products)),
      icon: getElementIconCategoryName(getElementCategory(categorySource)?.id),
      versionId: version.id,
    };
  }
};

const threadItemUserId = (item: CommentThreadItem): string =>
  item.kind === 'activity' ? item.userId : item.comment.owner;

type ActivityLink =
  | { kind: 'proposal'; versionId: string; proposalId: string }
  | { kind: 'version'; versionId: string }
  | { kind: 'storey'; buildingId: string; storeyId: string }
  | { kind: 'building'; buildingId: string };

type ActivityPart = {
  text: string;
  emphasis?: boolean;
  /** Keep the text on one line unless it is wider than the row. */
  intact?: boolean;
  link?: ActivityLink;
  /** Shown when the named proposal or version no longer exists. */
  removed?: string;
};

const activitySentence = (
  author: string,
  action: string,
  name: string,
  options?: { intactName?: boolean; link?: ActivityLink; removed?: string },
): ActivityPart[] => [
  { text: author, emphasis: true },
  { text: ` ${action} ` },
  {
    text: name,
    emphasis: true,
    intact: options?.intactName,
    link: options?.link,
    removed: options?.removed,
  },
];

const activityIdentity = (
  activity: ProjectEventActivity,
  actionProposalIds: ReadonlyMap<string, string>,
): string => {
  switch (activity.kind) {
    case 'proposal-created':
      return 'proposal-created';
    case 'version-created':
      return `version-created:${activity.versionId}`;
    case 'action-created':
      return `action-created:${actionProposalIds.get(activity.actionId) ?? activity.actionId}`;
    case 'baseline-edited':
      return `baseline-edited:${activity.versionId}`;
    case 'storey-footprint-edited':
      return `storey-footprint:${activity.buildingId}`;
    case 'storey-added':
      return `storey-added:${activity.buildingId}`;
    case 'building-moved':
      return 'building-moved';
    case 'building-created':
      return 'building-created';
    case 'building-deleted':
      return `building-deleted:${activity.buildingId}:${activity.name}`;
    case 'project-location':
      return 'project-location';
    case 'proposal-deleted':
      return `proposal-deleted:${activity.proposalId}:${activity.name}`;
    case 'version-deleted':
      return `version-deleted:${activity.versionId}:${activity.name}`;
    default:
      return assertUnreachable(activity);
  }
};

const activityLabel = (
  activity: ProjectEventActivity,
  author: string,
  names: {
    proposals: Map<string, string>;
    versions: Map<string, string>;
    actions: Map<string, string>;
    actionProposalIds: Map<string, string>;
    proposalVersionIds: Map<string, string>;
    buildings: Map<string, string>;
    buildingsWithGeometry: Set<string>;
    storeys: Map<string, string>;
    storeyNumbers: Map<string, number>;
  },
  count = 1,
  storeyIds?: readonly string[],
  movedBuildingIds?: readonly string[],
): ActivityPart[] | undefined => {
  switch (activity.kind) {
    case 'proposal-created': {
      if (count > 1) {
        return [
          { text: author, emphasis: true },
          { text: ' created ' },
          { text: `${count} proposals`, emphasis: true },
        ];
      }
      const name = names.proposals.get(activity.proposalId);
      if (!name) return;
      const versionId = names.proposalVersionIds.get(activity.proposalId);
      const versionName = versionId ? names.versions.get(versionId) : undefined;
      const sentence = activitySentence(author, 'created proposal', name, {
        link: versionId
          ? { kind: 'proposal', versionId, proposalId: activity.proposalId }
          : undefined,
      });
      if (!versionName || !versionId) return sentence;
      return [
        ...sentence,
        { text: ' in ' },
        {
          text: versionName,
          emphasis: true,
          intact: true,
          link: { kind: 'version', versionId },
        },
      ];
    }
    case 'version-created': {
      const name = names.versions.get(activity.versionId);
      if (!name) return;
      return activitySentence(author, 'created version', name, {
        intactName: true,
        link: { kind: 'version', versionId: activity.versionId },
      });
    }
    case 'action-created': {
      const name = names.actions.get(activity.actionId);
      if (!name) return;
      const proposalId = names.actionProposalIds.get(activity.actionId);
      const proposalName = proposalId
        ? names.proposals.get(proposalId)
        : undefined;
      const proposalVersionId = proposalId
        ? names.proposalVersionIds.get(proposalId)
        : undefined;
      if (!proposalId || !proposalName || !proposalVersionId) return;
      return [
        { text: author, emphasis: true },
        { text: ' created ' },
        { text: count > 1 ? `${count} actions` : 'an action', emphasis: true },
        { text: ' on ' },
        {
          text: proposalName,
          emphasis: true,
          intact: true,
          link: {
            kind: 'proposal' as const,
            versionId: proposalVersionId,
            proposalId,
          },
        },
      ];
    }
    case 'baseline-edited': {
      const name = names.versions.get(activity.versionId);
      if (!name) return;
      const lead = count > 1 ? ` made ${count} edits to the ` : ' edited the ';
      return [
        { text: author, emphasis: true },
        { text: lead },
        {
          text: name,
          emphasis: true,
          intact: true,
          link: { kind: 'version', versionId: activity.versionId },
        },
        { text: ' baseline' },
      ];
    }
    case 'storey-footprint-edited': {
      const buildingName = names.buildings.get(activity.buildingId);
      const phrase = footprintStoreyPhrase(activity, names, storeyIds);
      if (!buildingName || !phrase) return;
      const parts: ActivityPart[] = [
        { text: author, emphasis: true },
        { text: ' edited ' },
        {
          text: phrase.text,
          emphasis: true,
          link: {
            kind: 'storey',
            buildingId: activity.buildingId,
            storeyId: phrase.storeyId,
          },
        },
      ];
      if (names.buildings.size > 1) {
        parts.push(
          { text: ' in ' },
          {
            text: buildingName,
            emphasis: true,
            link: { kind: 'building', buildingId: activity.buildingId },
          },
        );
      }
      return parts;
    }
    case 'storey-added': {
      const buildingName = names.buildings.get(activity.buildingId);
      if (!buildingName) return;
      const action =
        count > 1 ? `added ${count} storeys to` : 'added a storey to';
      return activitySentence(author, action, buildingName, {
        link: { kind: 'building', buildingId: activity.buildingId },
      });
    }
    case 'building-moved': {
      const ids = [
        ...new Set(
          movedBuildingIds?.length ? movedBuildingIds : [activity.buildingId],
        ),
      ];
      const named = ids.flatMap((id) => {
        const name = names.buildings.get(id);
        return name ? [{ id, name }] : [];
      });
      const only = named[0];
      if (!only) return;
      if (named.length === 1) {
        return activitySentence(author, 'moved', only.name, {
          link: { kind: 'building', buildingId: only.id },
        });
      }
      return [
        { text: author, emphasis: true },
        { text: ' moved ' },
        { text: `${named.length} buildings`, emphasis: true },
      ];
    }
    case 'building-created': {
      const ids = [
        ...new Set(
          movedBuildingIds?.length ? movedBuildingIds : [activity.buildingId],
        ),
      ];
      const named = ids.flatMap((id) => {
        const name = names.buildings.get(id);
        if (!name || !names.buildingsWithGeometry.has(id)) return [];
        return [{ id, name }];
      });
      const only = named[0];
      if (!only) return;
      if (named.length === 1) {
        return activitySentence(author, 'created', only.name, {
          link: { kind: 'building', buildingId: only.id },
        });
      }
      return [
        { text: author, emphasis: true },
        { text: ' created ' },
        { text: `${named.length} buildings`, emphasis: true },
      ];
    }
    case 'building-deleted':
      return activitySentence(author, 'deleted', activity.name, {
        removed: 'This building has been deleted',
      });
    case 'project-location':
      return [
        { text: author, emphasis: true },
        { text: ' edited the project location' },
      ];
    case 'proposal-deleted':
      return activitySentence(author, 'deleted proposal', activity.name, {
        removed: 'This proposal has been deleted',
      });
    case 'version-deleted':
      return activitySentence(author, 'deleted version', activity.name, {
        intactName: true,
        removed: 'This version has been deleted',
      });
    default:
      return assertUnreachable(activity);
  }
};

/** `johan.svensson@nodon.se` → `Johan Svensson`. A stored name is kept as-is. */
const formatCommentAuthorName = (
  name: string | undefined,
): string | undefined => {
  const trimmed = name?.trim();
  if (!trimmed) return;
  const at = trimmed.indexOf('@');
  if (at <= 0) return trimmed;
  const localPart = trimmed.slice(0, at).split('+')[0] ?? '';
  const parts = localPart.split('.').filter((part) => part.length > 0);
  if (!parts.length) return;
  return parts
    .map((part) => part.charAt(0).toUpperCase() + part.slice(1).toLowerCase())
    .join(' ');
};

const useStyles = makeStyles()((theme) => ({
  panel: {
    position: 'relative',
    display: 'flex',
    flex: '1 1 0',
    flexDirection: 'column',
    minHeight: 0,
    overflow: 'hidden',
  },
  content: {
    cursor: 'default',
    height: '100%',
    overflowY: 'auto',
    overflowAnchor: 'none',
    scrollbarWidth: 'none',
    '&::-webkit-scrollbar': {
      display: 'none',
      width: 0,
      height: 0,
    },
    paddingTop: 8,
    paddingBottom: `calc(var(${DESIGN_COMMENTS_INPUT_HEIGHT_PROPERTY}, 12px) + 24px)`,
    display: 'flex',
    flexDirection: 'column',
  },
  unseen: {
    display: 'flex',
    flexDirection: 'column',
    flex: '1 0 auto',
    backgroundColor: 'rgba(223, 240, 255, 0.5)',
    borderTop: `1px solid color-mix(in srgb, ${theme.palette.custom.focus} 10%, transparent)`,
    marginBottom: `calc(-1 * (var(${DESIGN_COMMENTS_INPUT_HEIGHT_PROPERTY}, 12px) + 24px))`,
    paddingBottom: `calc(var(${DESIGN_COMMENTS_INPUT_HEIGHT_PROPERTY}, 12px) + 24px)`,
  },
  listFrame: {
    position: 'relative',
    flex: '1 0 0',
    minHeight: 0,
  },
  empty: {
    '&.MuiTypography-root': {
      margin: 0,
    },
    flex: '1 0 auto',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    padding: '24px 32px',
    fontSize: 12,
    lineHeight: '16px',
    fontWeight: 400,
    letterSpacing: '0.2px',
    color: 'rgba(0,0,0,0.45)',
    textAlign: 'center',
  },
  messageTimestamp: {
    '&.MuiTypography-root': {
      margin: 0,
    },
    paddingTop: 14,
    paddingBottom: 8,
    fontSize: 12,
    lineHeight: '16px',
    fontWeight: 400,
    letterSpacing: '0.2px',
    color: 'rgba(0,0,0,0.45)',
    textAlign: 'center',
  },
  activity: {
    '&.MuiTypography-root': {
      margin: 0,
      fontSize: 11,
      lineHeight: '14px',
    },
    paddingTop: 6,
    paddingBottom: 6,
    paddingLeft: 16,
    paddingRight: 56,
    fontWeight: 400,
    letterSpacing: '0.2px',
    color: 'rgba(0,0,0,0.45)',
    textAlign: 'left',
  },
  activityEmphasis: {
    fontWeight: 500,
  },
  activityLink: {
    padding: 0,
    border: 'none',
    background: 'none',
    fontFamily: 'inherit',
    fontSize: 'inherit',
    lineHeight: 'inherit',
    letterSpacing: 'inherit',
    color: 'inherit',
    cursor: 'pointer',
    '&:hover': {
      color: '#000',
    },
  },
  activityIntact: {
    display: 'inline-block',
    maxWidth: '100%',
  },
  activityRow: {
    position: 'relative',
  },
  activityWithCaret: {
    paddingLeft: 26,
  },
  activityCaret: {
    position: 'absolute',
    left: 12,
    top: 6,
    display: 'flex',
    padding: 0,
    border: 'none',
    background: 'none',
    color: 'rgba(0,0,0,0.45)',
    cursor: 'pointer',
  },
  activityHoverTime: {
    top: '50%',
    right: 16,
    transform: 'translateY(-50%)',
  },
  messageHoverTime: {
    position: 'absolute',
    margin: 0,
    opacity: 0,
    pointerEvents: 'none',
    '&.MuiTypography-root': {
      margin: 0,
    },
    fontSize: 11,
    lineHeight: '12px',
    fontWeight: 400,
    letterSpacing: '0.2px',
    color: 'rgba(0,0,0,0.45)',
  },
  userMessageHoverTime: {
    top: 'calc(100% - 4px)',
    right: 16,
  },
  userMessageRow: {
    position: 'relative',
    display: 'flex',
    justifyContent: 'flex-end',
    paddingLeft: 36,
    paddingRight: 16,
    paddingTop: 10,
    paddingBottom: 8,
    flexShrink: 0,
  },
  otherMessageRow: {
    position: 'relative',
    display: 'flex',
    justifyContent: 'flex-start',
    paddingLeft: 16,
    paddingRight: 36,
    paddingTop: 10,
    paddingBottom: 8,
    flexShrink: 0,
  },
  userBubble: {
    ...getTooltipBaseStyles(theme),
    backgroundColor: 'rgba(40,42,49,0.8)',
    display: 'flex',
    flexDirection: 'column',
    gap: 0,
    padding: '8px 12px',
    maxWidth: '100%',
    borderRadius: '12px 12px 2px 12px',
    boxShadow: 'none',
  },
  commentPart: {
    display: 'flex',
    flexDirection: 'column',
    gap: 6,
  },
  otherBubble: {
    borderRadius: '2px 12px 12px 12px',
    backgroundColor: 'rgba(255, 255, 255, 0.8)',
    border: '1px solid rgba(0, 0, 0, 0.1)',
    color: 'rgba(0, 0, 0, 0.8)',
  },
  elementCommentLabel: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'flex-start',
    gap: 4,
    width: '100%',
    maxWidth: '100%',
    minWidth: 0,
    padding: 0,
    border: 'none',
    background: 'none',
    fontFamily: 'inherit',
    fontSize: 11,
    lineHeight: '14px',
    letterSpacing: '0.2px',
    marginBottom: -4,
    textAlign: 'left',
    color: 'rgba(255, 255, 255, 0.7)',
    cursor: 'pointer',
    '&:hover': {
      color: '#fff',
    },
  },
  elementCommentLabelOther: {
    color: 'rgba(0, 0, 0, 0.7)',
    '&:hover': {
      color: '#000',
    },
  },
  elementCommentName: {
    overflow: 'hidden',
    minWidth: 0,
    textOverflow: 'ellipsis',
    whiteSpace: 'nowrap',
    '&.MuiTypography-root': {
      margin: 0,
      color: 'inherit',
      fontSize: 'inherit',
      fontWeight: 500,
      letterSpacing: 'inherit',
      lineHeight: 'inherit',
    },
  },
  userText: {
    '&.MuiTypography-root': {
      margin: 0,
      color: 'inherit',
      fontSize: 'inherit',
      fontWeight: 'inherit',
      letterSpacing: 'inherit',
      lineHeight: 'inherit',
    },
    overflowWrap: 'anywhere',
    whiteSpace: 'pre-wrap',
  },
  authorColumn: {
    display: 'flex',
    flexDirection: 'column',
    maxWidth: '100%',
    minWidth: 0,
  },
  authorColumnMine: {
    alignItems: 'flex-end',
  },
  authorColumnOther: {
    alignItems: 'flex-start',
  },
  authorName: {
    '&.MuiTypography-root': {
      margin: 0,
    },
    fontSize: 11,
    lineHeight: '12px',
    fontWeight: 400,
    letterSpacing: '0.2px',
    color: 'rgba(0,0,0,0.45)',
  },
  authorMeta: {
    position: 'relative',
    display: 'flex',
    alignItems: 'baseline',
    marginTop: -2,
    marginBottom: 4,
    paddingLeft: 3,
  },
  authorTime: {
    position: 'absolute',
    left: '100%',
    opacity: 0,
    pointerEvents: 'none',
    whiteSpace: 'nowrap',
    '&.MuiTypography-root': {
      margin: 0,
      marginLeft: 6,
    },
  },
  authorTimeVisible: {
    opacity: 1,
  },
  panelFooter: {
    position: 'absolute',
    right: 0,
    bottom: 0,
    left: 0,
    zIndex: 1,
    display: 'flex',
    flexDirection: 'column',
    gap: 4,
    padding: '6px 10px 10px',
    pointerEvents: 'none',
    '& > *': {
      pointerEvents: 'auto',
    },
  },
  inputWrapper: {
    position: 'relative',
    backgroundColor: theme.palette.background.default,
    border: '1px solid rgba(0,0,0,0.1)',
    borderRadius: 16,
    boxShadow: '0 4px 16px rgba(0, 0, 0, 0.08)',
    '&:focus-within': {
      backgroundColor: '#fff',
      borderColor: '#0B99FF',
    },
  },
  input: {
    padding: '10px 36px 30px 12px',
    fontSize: 13,
    letterSpacing: '0.52px',
    lineHeight: '17px',
    color: 'rgba(0,0,0,0.8)',
    width: '100%',
  },
  submitButton: {
    position: 'absolute',
    bottom: 6,
    right: 6,
    width: 18,
    height: 18,
    padding: 0,
    borderRadius: '50%',
    backgroundColor: 'rgba(0,0,0,0.8)',
    color: '#fff',
    '&:hover': {
      backgroundColor: 'rgba(0,0,0,0.9)',
    },
    '&.Mui-disabled': {
      opacity: 0.2,
      backgroundColor: 'rgba(0,0,0,0.8)',
      color: '#fff',
    },
  },
  sendError: {
    paddingLeft: 4,
    fontSize: 11,
    color: theme.palette.error.main,
    letterSpacing: '0.44px',
  },
}));

/**
 * Project comments in the design right panel, laid out like the agent thread.
 * User comments sit on the right. Writing one does not ask the agent to reply.
 */
export const DesignCommentsPanel: React.FC = () => {
  const { classes, cx } = useStyles();
  const project = getProject();
  const liveProject = useProject();
  const buildingDesign = useBuildingDesign();
  const user = useUser();
  const usersLookup = useUserLookup();
  const comments = useCommentsStore((state) => state.comments);
  const events = useProjectEvents();
  const createComment = useCommentsStore((state) => state.createComment);
  const changeCommentGroup = useCommentsStore(
    (state) => state.changeCommentGroup,
  );
  const [editingCommentId, setEditingCommentId] = useState<string>();
  const { enqueueSnackbar, closeSnackbar } = useSnackbar();
  const listRef = useRef<HTMLDivElement>(null);
  const listFrameRef = useRef<HTMLDivElement>(null);
  const expandAnchorRef = useRef<{ runId: string; top: number } | undefined>(
    undefined,
  );
  const footerRef = useRef<HTMLDivElement>(null);
  const [draft, setDraft] = useState(() => readCommentDraft(project.id));
  const draftRef = useRef(draft);
  draftRef.current = draft;
  const projectIdRef = useRef(project.id);
  const [unseenSince] = useState(
    () => localStorage.getItem(commentSeenKey(project.id)) ?? '',
  );
  const [sending, setSending] = useState(false);
  const [sendError, setSendError] = useState<string>();
  const [hoveredCommentId, setHoveredCommentId] = useState<string>();
  const [expandedActivityRuns, setExpandedActivityRuns] = useState<
    ReadonlySet<string>
  >(() => new Set());

  const activityNames = useMemo(() => {
    const proposals = new Map<string, string>();
    const versions = new Map<string, string>();
    const actions = new Map<string, string>();
    const actionProposalIds = new Map<string, string>();
    const proposalVersionIds = new Map<string, string>();
    const buildings = new Map<string, string>();
    const buildingsWithGeometry = new Set<string>();
    const storeys = new Map<string, string>();
    const storeyNumbers = new Map<string, number>();
    for (const version of liveProject.baselines) {
      versions.set(version.id, version.name);
      for (const proposal of getProposals(version)) {
        proposals.set(proposal.id, proposal.name);
        proposalVersionIds.set(proposal.id, version.id);
        for (const actionId of proposal.actionIds) {
          actionProposalIds.set(actionId, proposal.id);
        }
      }
      for (const action of version.actions ?? []) {
        const name =
          action.name?.trim() || buildActionName(action.changes, version);
        if (name) actions.set(action.id, name);
      }
    }
    buildingDesign.buildings.forEach((building, index) => {
      buildings.set(building.id, getBuildingDisplayName(building, index));
      if (!isEmptyBuildingGeometry(building)) {
        buildingsWithGeometry.add(building.id);
      }
      for (const storey of building.storeys) {
        if (storey.name) storeys.set(storey.id, storey.name);
        storeyNumbers.set(storey.id, getStoreyNumber(storey, building.storeys));
      }
    });
    const elements = new Map<
      string,
      {
        name: string;
        icon: ReturnType<typeof getElementIconCategoryName>;
        versionId: string;
      }
    >();
    for (const comment of comments) {
      const elementId = comment.element_id;
      if (!elementId || elements.has(elementId)) continue;
      const context = elementCommentContext(liveProject, elementId);
      if (context) elements.set(elementId, context);
    }
    return {
      proposals,
      versions,
      actions,
      actionProposalIds,
      proposalVersionIds,
      buildings,
      buildingsWithGeometry,
      storeys,
      storeyNumbers,
      elements,
    };
  }, [liveProject, buildingDesign, comments]);

  const thread = useMemo(() => {
    const items: CommentThreadItem[] = [];
    for (const comment of comments) {
      if (!isUserComment(comment)) continue;
      items.push({ kind: 'comment', comment });
    }
    for (const event of events) {
      const item = eventThreadItem(event);
      const ownerName =
        usersLookup[item.userId]?.name ??
        (item.userId === user?.id ? user?.name : undefined);
      const author = formatCommentAuthorName(ownerName);
      // Hidden activities cannot own the first visible row's clock separator.
      if (author && activityLabel(item.activity, author, activityNames, 1)) {
        items.push(item);
      }
    }
    items.sort((a, b) =>
      a.comment.created_at.localeCompare(b.comment.created_at),
    );
    return items;
  }, [comments, events, usersLookup, user, activityNames]);

  const trimmedDraft = draft.trim();
  const canSend = Boolean(user) && trimmedDraft.length > 0 && !sending;

  useLayoutEffect(() => {
    if (projectIdRef.current === project.id) return;
    writeCommentDraft(projectIdRef.current, draftRef.current);
    projectIdRef.current = project.id;
    const stored = readCommentDraft(project.id);
    draftRef.current = stored;
    setDraft(stored);
  }, [project.id]);

  useLayoutEffect(
    () => () => writeCommentDraft(projectIdRef.current, draftRef.current),
    [],
  );

  useLayoutEffect(() => {
    const node = listRef.current;
    if (!node) return;
    node.scrollTop = node.scrollHeight;
  }, [thread.length]);

  useLayoutEffect(() => {
    const anchor = expandAnchorRef.current;
    const list = listRef.current;
    if (!anchor || !list) return;
    expandAnchorRef.current = undefined;
    const row = list.querySelector(
      `[data-activity-run="${CSS.escape(anchor.runId)}"]`,
    );
    if (!row) return;
    list.scrollTop += row.getBoundingClientRect().top - anchor.top;
  }, [expandedActivityRuns]);

  useLayoutEffect(() => {
    const footer = footerRef.current;
    const frame = footer?.parentElement;
    if (!footer || !(frame instanceof HTMLElement)) return;

    const syncHeight = () => {
      frame.style.setProperty(
        DESIGN_COMMENTS_INPUT_HEIGHT_PROPERTY,
        `${footer.offsetHeight}px`,
      );
    };
    syncHeight();
    const observer = new ResizeObserver(syncHeight);
    observer.observe(footer);
    return () => {
      observer.disconnect();
      frame.style.removeProperty(DESIGN_COMMENTS_INPUT_HEIGHT_PROPERTY);
    };
  }, []);

  const onDraftChange = useCallback<DraftChangeHandler>((value) => {
    setDraft(value);
    setSendError(undefined);
  }, []);

  const onSend = useCallback(async () => {
    if (!user || !trimmedDraft || sending) return;
    setSending(true);
    setSendError(undefined);
    const comment = createUserComment({
      project_id: project.id,
      owner: user.id,
      message: trimmedDraft,
    });
    try {
      await createComment(comment, CREATE_ERROR_ID);
      draftRef.current = '';
      writeCommentDraft(project.id, '');
      setDraft('');
    } catch (error) {
      setSendError(
        error instanceof Error ? error.message : 'Could not add comment',
      );
    } finally {
      setSending(false);
    }
  }, [createComment, project.id, sending, trimmedDraft, user]);

  const onDraftKeyDown = useCallback(
    (event: KeyboardEvent<HTMLInputElement | HTMLTextAreaElement>) => {
      if (event.key !== 'Enter' || event.shiftKey) return;
      event.preventDefault();
      void onSend();
    },
    [onSend],
  );

  const onCommentHover = useCallback<CommentHoverHandler>((commentId) => {
    setHoveredCommentId(commentId);
  }, []);

  const onCommentHoverEnd = useCallback(() => {
    setHoveredCommentId(undefined);
  }, []);

  const navigateTo = useProjectNavigation();
  const selectBuilding = useSelectBuilding();
  const selectBuildingOutline = useSelectBuildingOutline();
  const setSelectedStoreyId = useSetSelectedStoreyId();
  const setRightPanelTab = useSetDesignRightPanelTab();
  const openDesignSelection = useCallback(() => {
    setRightPanelTab('comments');
    void navigateTo(
      { page: Page.Design, elementId: URL_CATEGORY_NONE },
      { searchParams: { [PROPOSAL_SEARCH_PARAM]: null } },
    );
  }, [navigateTo, setRightPanelTab]);
  const openActivityLink = useCallback(
    (link: ActivityLink) => {
      switch (link.kind) {
        case 'proposal':
        case 'version': {
          const proposalId = link.kind === 'proposal' ? link.proposalId : null;
          void navigateTo(
            {
              page: Page.Elements,
              versionId: link.versionId,
              elementId: URL_CATEGORY_NONE,
            },
            { searchParams: { [PROPOSAL_SEARCH_PARAM]: proposalId } },
          );
          return;
        }
        case 'storey':
          selectBuilding(link.buildingId);
          setSelectedStoreyId(link.storeyId);
          openDesignSelection();
          return;
        case 'building':
          selectBuilding(link.buildingId);
          selectBuildingOutline(link.buildingId);
          openDesignSelection();
          return;
        default:
          return assertUnreachable(link);
      }
    },
    [
      navigateTo,
      openDesignSelection,
      selectBuilding,
      selectBuildingOutline,
      setSelectedStoreyId,
    ],
  );

  const toggleActivityRun = useCallback((runId: string) => {
    const list = listRef.current;
    const row = list?.querySelector(
      `[data-activity-run="${CSS.escape(runId)}"]`,
    );
    if (row) {
      expandAnchorRef.current = {
        runId,
        top: row.getBoundingClientRect().top,
      };
    }
    setExpandedActivityRuns((current) => {
      const next = new Set(current);
      if (next.has(runId)) next.delete(runId);
      else next.add(runId);
      return next;
    });
  }, []);

  const isNewlyUnseen = (item: CommentThreadItem): boolean => {
    const userId = threadItemUserId(item);
    return (
      user !== undefined &&
      userId !== user.id &&
      userId !== OPEN_AI_USER_ID &&
      (item.kind === 'activity' ? item.changedAt : item.comment.created_at) >
        unseenSince
    );
  };
  const crossesUnseen = (
    earlier: CommentThreadItem,
    later: CommentThreadItem,
  ): boolean => !isNewlyUnseen(earlier) && isNewlyUnseen(later);
  let firstUnseenIndex = thread.findIndex(isNewlyUnseen);
  // The unread background starts at the bubble that owns the first unseen comment.
  while (
    firstUnseenIndex > 0 &&
    thread[firstUnseenIndex]?.kind === 'comment' &&
    !commentBubbleRun(thread, firstUnseenIndex).length
  )
    firstUnseenIndex--;
  const now = new Date();

  return (
    <Box className={classes.panel}>
      <Box ref={listFrameRef} className={classes.listFrame}>
        <Box ref={listRef} className={classes.content}>
          {thread.length === 0 && (
            <Typography className={classes.empty} component="p">
              Comments and activity on this project show up here.
            </Typography>
          )}
          {(() => {
            const nodes = thread.map((item, index) => {
              const timestamp = historyTimestampLabel(
                item.comment.created_at,
                thread[index - 1]?.comment.created_at,
                now,
              );
              if (item.kind === 'activity') {
                const previous = thread[index - 1];
                const continuesRun =
                  previous?.kind === 'activity' &&
                  previous.comment.owner === item.comment.owner &&
                  activityIdentity(
                    previous.activity,
                    activityNames.actionProposalIds,
                  ) ===
                    activityIdentity(
                      item.activity,
                      activityNames.actionProposalIds,
                    ) &&
                  !shouldShowChatTimestamp(
                    item.comment.created_at,
                    previous.comment.created_at,
                  ) &&
                  !crossesUnseen(previous, item);
                if (continuesRun) return null;
                const run = [item];
                for (
                  let cursor = index + 1;
                  cursor < thread.length;
                  cursor += 1
                ) {
                  const next = thread[cursor];
                  if (next?.kind !== 'activity') break;
                  if (next.comment.owner !== item.comment.owner) break;
                  if (
                    shouldShowChatTimestamp(
                      next.comment.created_at,
                      run[run.length - 1]?.comment.created_at,
                    ) ||
                    crossesUnseen(item, next)
                  ) {
                    break;
                  }
                  if (
                    activityIdentity(
                      next.activity,
                      activityNames.actionProposalIds,
                    ) !==
                    activityIdentity(
                      item.activity,
                      activityNames.actionProposalIds,
                    )
                  ) {
                    break;
                  }
                  run.push(next);
                }
                const runId = item.comment.id;
                const expanded = expandedActivityRuns.has(runId);
                const ownerName =
                  usersLookup[item.userId]?.name ??
                  (item.userId === user?.id ? user?.name : undefined);
                const author = formatCommentAuthorName(ownerName);
                const renderActivity = (
                  source: (typeof run)[number],
                  options: {
                    count: number;
                    caret: 'closed' | 'none';
                    actionNames?: readonly string[];
                    storeyIds?: readonly string[];
                    movedBuildingIds?: readonly string[];
                  },
                ) => {
                  const label = author
                    ? activityLabel(
                        source.activity,
                        author,
                        activityNames,
                        options.count,
                        options.storeyIds,
                        options.movedBuildingIds,
                      )
                    : undefined;
                  if (!label) return null;
                  const hoverTime =
                    hoveredCommentId === source.comment.id
                      ? formatChatMessageTime(source.comment.created_at)
                      : undefined;
                  return (
                    <Tooltip
                      title={
                        options.actionNames?.length ? (
                          <>
                            {options.actionNames.map(
                              (actionName, actionIndex) => (
                                <Box key={actionIndex} component="div">
                                  {actionName}
                                </Box>
                              ),
                            )}
                          </>
                        ) : (
                          ''
                        )
                      }
                      placement="top"
                      disableHoverListener={!options.actionNames?.length}
                    >
                      <Box
                        key={source.comment.id}
                        data-activity-run={runId}
                        className={classes.activityRow}
                        onMouseEnter={() => onCommentHover(source.comment.id)}
                        onMouseLeave={onCommentHoverEnd}
                      >
                        {options.caret !== 'none' && (
                          <button
                            type="button"
                            className={classes.activityCaret}
                            aria-expanded={false}
                            aria-label="Expand activities"
                            onClick={() => toggleActivityRun(runId)}
                          >
                            <KeyboardArrowRight sx={{ fontSize: 12 }} />
                          </button>
                        )}
                        <Typography
                          className={cx(
                            classes.activity,
                            options.caret !== 'none' &&
                              classes.activityWithCaret,
                          )}
                          component="p"
                        >
                          {label.map((part, partIndex) => {
                            const link = part.link;
                            const name = link ? (
                              <Box
                                key={partIndex}
                                component="button"
                                type="button"
                                className={cx(
                                  classes.activityEmphasis,
                                  part.intact && classes.activityIntact,
                                  classes.activityLink,
                                )}
                                onClick={(event) => {
                                  event.stopPropagation();
                                  openActivityLink(link);
                                }}
                              >
                                {part.text}
                              </Box>
                            ) : (
                              <Box
                                key={partIndex}
                                component="span"
                                className={cx(
                                  classes.activityEmphasis,
                                  part.intact && classes.activityIntact,
                                )}
                              >
                                {part.text}
                              </Box>
                            );
                            if (!part.emphasis) {
                              return (
                                <Fragment key={partIndex}>{part.text}</Fragment>
                              );
                            }
                            if (!part.removed) return name;
                            return (
                              <Tooltip
                                key={partIndex}
                                title={part.removed}
                                placement="top"
                              >
                                {name}
                              </Tooltip>
                            );
                          })}
                        </Typography>
                        {hoverTime && (
                          <Typography
                            className={cx(
                              classes.messageHoverTime,
                              classes.activityHoverTime,
                              classes.authorTimeVisible,
                            )}
                            component="p"
                          >
                            {hoverTime}
                          </Typography>
                        )}
                      </Box>
                    </Tooltip>
                  );
                };
                if (!author) return null;
                const visibleRun = run.filter((entry) =>
                  activityLabel(entry.activity, author, activityNames, 1),
                );
                if (visibleRun.length === 0) return null;
                const staysCollapsed = isSummaryOnlyActivity(
                  item.activity.kind,
                );
                const collapsed =
                  visibleRun.length > 1 && (staysCollapsed || !expanded);
                if (collapsed) {
                  const latest = visibleRun[visibleRun.length - 1];
                  if (!latest) return null;
                  return (
                    <Fragment key={runId}>
                      {timestamp && (
                        <Typography
                          className={classes.messageTimestamp}
                          component="p"
                        >
                          {timestamp}
                        </Typography>
                      )}
                      {renderActivity(latest, {
                        count: visibleRun.length,
                        caret: staysCollapsed ? 'none' : 'closed',
                        storeyIds:
                          latest.activity.kind === 'storey-footprint-edited'
                            ? visibleRun.flatMap((entry) =>
                                entry.activity.kind ===
                                'storey-footprint-edited'
                                  ? footprintStoreyIds(entry.activity)
                                  : [],
                              )
                            : undefined,
                        movedBuildingIds:
                          latest.activity.kind === 'building-moved' ||
                          latest.activity.kind === 'building-created'
                            ? visibleRun.flatMap((entry) => {
                                if (
                                  entry.activity.kind !== 'building-moved' &&
                                  entry.activity.kind !== 'building-created'
                                ) {
                                  return [];
                                }
                                if (
                                  entry.activity.kind !== latest.activity.kind
                                ) {
                                  return [];
                                }
                                return [entry.activity.buildingId];
                              })
                            : undefined,
                        actionNames: (() => {
                          if (latest.activity.kind === 'action-created') {
                            return visibleRun.flatMap((entry) => {
                              if (entry.activity.kind !== 'action-created')
                                return [];
                              const actionName = activityNames.actions.get(
                                entry.activity.actionId,
                              );
                              return actionName ? [actionName] : [];
                            });
                          }
                          if (
                            latest.activity.kind !== 'building-moved' &&
                            latest.activity.kind !== 'building-created'
                          ) {
                            return;
                          }
                          const seen = new Set<string>();
                          const buildingNames = visibleRun.flatMap((entry) => {
                            if (
                              entry.activity.kind !== 'building-moved' &&
                              entry.activity.kind !== 'building-created'
                            ) {
                              return [];
                            }
                            if (entry.activity.kind !== latest.activity.kind) {
                              return [];
                            }
                            if (seen.has(entry.activity.buildingId)) return [];
                            seen.add(entry.activity.buildingId);
                            const buildingName = activityNames.buildings.get(
                              entry.activity.buildingId,
                            );
                            if (
                              latest.activity.kind === 'building-created' &&
                              !activityNames.buildingsWithGeometry.has(
                                entry.activity.buildingId,
                              )
                            ) {
                              return [];
                            }
                            return buildingName ? [buildingName] : [];
                          });
                          return buildingNames.length > 1
                            ? buildingNames
                            : undefined;
                        })(),
                      })}
                    </Fragment>
                  );
                }
                return (
                  <Fragment key={runId}>
                    {visibleRun.map((entry, entryIndex) => {
                      const entryTimestamp =
                        entryIndex === 0
                          ? timestamp
                          : historyTimestampLabel(
                              entry.comment.created_at,
                              visibleRun[entryIndex - 1]?.comment.created_at,
                              now,
                            );
                      return (
                        <Fragment key={entry.comment.id}>
                          {entryTimestamp && (
                            <Typography
                              className={classes.messageTimestamp}
                              component="p"
                            >
                              {entryTimestamp}
                            </Typography>
                          )}
                          {renderActivity(entry, {
                            count: 1,
                            caret: 'none',
                            actionNames: (() => {
                              if (entry.activity.kind !== 'action-created')
                                return;
                              const actionName = activityNames.actions.get(
                                entry.activity.actionId,
                              );
                              return actionName ? [actionName] : undefined;
                            })(),
                          })}
                        </Fragment>
                      );
                    })}
                  </Fragment>
                );
              }
              const comment = item.comment;
              const isMine = comment.owner === user?.id;
              const groupedComments = commentBubbleRun(thread, index);
              if (!groupedComments.length) return null;
              const authorName = isMine
                ? undefined
                : formatCommentAuthorName(usersLookup[comment.owner]?.name);
              const groupHoverTime = groupedComments.some(
                (entry) => entry.id === hoveredCommentId,
              )
                ? formatChatMessageTime(
                    groupedComments[groupedComments.length - 1]?.updated_at ||
                      groupedComments[groupedComments.length - 1]?.created_at ||
                      comment.created_at,
                  )
                : undefined;
              const headerHoverTime = authorName ? groupHoverTime : undefined;
              const ownGroupHoverTime = isMine ? groupHoverTime : undefined;

              return (
                <Fragment key={comment.id}>
                  {timestamp && (
                    <Typography
                      className={classes.messageTimestamp}
                      component="p"
                    >
                      {timestamp}
                    </Typography>
                  )}
                  <Box
                    className={cx(
                      isMine ? classes.userMessageRow : classes.otherMessageRow,
                    )}
                    onMouseEnter={() => onCommentHover(comment.id)}
                    onMouseLeave={onCommentHoverEnd}
                  >
                    <Box
                      className={cx(
                        classes.authorColumn,
                        isMine
                          ? classes.authorColumnMine
                          : classes.authorColumnOther,
                      )}
                    >
                      {authorName && (
                        <Box className={classes.authorMeta}>
                          <Typography
                            className={classes.authorName}
                            component="p"
                          >
                            {authorName}
                          </Typography>
                          {headerHoverTime && (
                            <Typography
                              className={cx(
                                classes.authorName,
                                classes.authorTime,
                                classes.authorTimeVisible,
                              )}
                              component="p"
                            >
                              {headerHoverTime}
                            </Typography>
                          )}
                        </Box>
                      )}

                      <EditableHistoryComment
                        message={groupedComments
                          .map((entry) => entry.message)
                          .join('\n')}
                        editable={isMine}
                        editing={editingCommentId === comment.id}
                        onEditingChange={(editing) => {
                          setEditingCommentId((current) =>
                            editing
                              ? comment.id
                              : current === comment.id
                                ? undefined
                                : current,
                          );
                        }}
                        onDelete={async () => {
                          const ids = groupedComments.map((entry) => entry.id);
                          await changeCommentGroup({ action: 'delete', ids });
                          setEditingCommentId((current) =>
                            current === comment.id ? undefined : current,
                          );
                          let restoring = false;
                          const showUndo = (failed = false) => {
                            enqueueSnackbar(
                              failed
                                ? 'Could not restore comment. Try again.'
                                : 'Comment deleted',
                              {
                                autoHideDuration: 8000,
                                persist: failed,
                                variant: failed ? 'error' : 'default',
                                action: (snackbarId) => (
                                  <Button
                                    variant="text"
                                    size="small"
                                    onClick={async () => {
                                      if (restoring) return;
                                      restoring = true;
                                      try {
                                        await changeCommentGroup({
                                          action: 'restore',
                                          ids,
                                        });
                                        closeSnackbar(snackbarId);
                                      } catch {
                                        showUndo(true);
                                      } finally {
                                        restoring = false;
                                      }
                                    }}
                                  >
                                    Undo
                                  </Button>
                                ),
                              },
                            );
                          };
                          showUndo();
                        }}
                        onSave={(message) =>
                          changeCommentGroup({
                            action: 'edit',
                            ids: groupedComments.map((entry) => entry.id),
                            message,
                          })
                        }
                      >
                        <Box
                          className={cx(
                            classes.userBubble,
                            !isMine && classes.otherBubble,
                          )}
                        >
                          {groupedComments.map((comment) => {
                            const elementLabel = comment.element_id
                              ? activityNames.elements.get(comment.element_id)
                              : undefined;
                            return (
                              <Box
                                key={comment.id}
                                className={classes.commentPart}
                              >
                                {elementLabel && comment.element_id && (
                                  <Tooltip
                                    title={elementLabel.name}
                                    placement="left"
                                  >
                                    <Box
                                      component="button"
                                      type="button"
                                      className={cx(
                                        classes.elementCommentLabel,
                                        !isMine &&
                                          classes.elementCommentLabelOther,
                                      )}
                                      onClick={() => {
                                        const elementId = comment.element_id;
                                        if (!elementId) return;
                                        void navigateTo(
                                          {
                                            page: Page.Elements,
                                            versionId: elementLabel.versionId,
                                            elementId,
                                          },
                                          {
                                            searchParams: {
                                              [PROPOSAL_SEARCH_PARAM]: null,
                                            },
                                          },
                                        );
                                      }}
                                    >
                                      <Icon
                                        category="elementCategory"
                                        icon={elementLabel.icon}
                                        size={12}
                                      />
                                      <Typography
                                        className={classes.elementCommentName}
                                        component="span"
                                      >
                                        {elementLabel.name}
                                      </Typography>
                                    </Box>
                                  </Tooltip>
                                )}
                                <Typography
                                  className={classes.userText}
                                  component="p"
                                >
                                  {comment.message}
                                </Typography>
                              </Box>
                            );
                          })}
                        </Box>
                      </EditableHistoryComment>
                    </Box>
                    {ownGroupHoverTime && (
                      <Typography
                        className={cx(
                          classes.messageHoverTime,
                          classes.userMessageHoverTime,
                          classes.authorTimeVisible,
                        )}
                        component="p"
                      >
                        {ownGroupHoverTime}
                      </Typography>
                    )}
                  </Box>
                </Fragment>
              );
            });
            if (firstUnseenIndex < 0) return nodes;
            return (
              <>
                {nodes.slice(0, firstUnseenIndex)}
                <Box className={classes.unseen}>
                  {nodes.slice(firstUnseenIndex)}
                </Box>
              </>
            );
          })()}
        </Box>
        <VerticalFadeScrollbar scrollRef={listRef} wrapperRef={listFrameRef} />
      </Box>
      <Box ref={footerRef} className={classes.panelFooter}>
        {sendError && (
          <Typography className={classes.sendError} component="p">
            {sendError}
          </Typography>
        )}
        <Box className={classes.inputWrapper}>
          <InputBase
            className={classes.input}
            multiline
            minRows={1}
            maxRows={6}
            placeholder="Add a comment..."
            value={draft}
            onChange={(event) => onDraftChange(event.target.value)}
            onKeyDown={onDraftKeyDown}
            disabled={!user || sending}
            inputProps={{ style: { resize: 'none' } }}
          />
          <Tooltip title="Send" placement="top">
            <span>
              <IconButton
                className={classes.submitButton}
                size="small"
                onClick={() => void onSend()}
                disabled={!canSend}
              >
                <North sx={{ fontSize: 12 }} />
              </IconButton>
            </span>
          </Tooltip>
        </Box>
      </Box>
    </Box>
  );
};
